/** The distilled metadata the gateway sends (see gateway `erp/meta.py`). */

export type FieldType =
  | 'Data' | 'Small Text' | 'Text' | 'Long Text' | 'Code' | 'Text Editor'
  | 'HTML Editor' | 'Markdown Editor' | 'Password' | 'Read Only' | 'Barcode'
  | 'Int' | 'Float' | 'Currency' | 'Percent' | 'Duration'
  | 'Check' | 'Select' | 'Autocomplete' | 'Color' | 'Icon'
  | 'Link' | 'Dynamic Link' | 'Table' | 'Table MultiSelect'
  | 'Date' | 'Datetime' | 'Time'
  | 'Attach' | 'Attach Image' | 'Image' | 'Signature'
  | 'Section Break' | 'Column Break' | 'Tab Break' | 'Heading' | 'HTML'
  | 'Button' | 'JSON' | 'Geolocation'

export type DocField = {
  fieldname: string
  fieldtype: FieldType
  label?: string
  options?: string
  default?: string
  description?: string
  reqd?: 1
  read_only?: 1
  hidden?: 1
  permlevel?: number
  precision?: string | number
  length?: number
  depends_on?: string
  mandatory_depends_on?: string
  read_only_depends_on?: string
  fetch_from?: string
  fetch_if_empty?: 1
  link_filters?: Record<string, unknown>
  in_list_view?: 1
  in_standard_filter?: 1
  in_preview?: 1
  allow_on_submit?: 1
  collapsible?: 1
  collapsible_depends_on?: string
  columns?: number
  bold?: 1
  non_negative?: 1
  unique?: 1
  no_copy?: 1
  print_hide?: 1
  hide_days?: 1
  hide_seconds?: 1
  sort_options?: 1
}

export type Permissions = Record<
  'read' | 'write' | 'create' | 'delete' | 'submit' | 'cancel' | 'amend'
  | 'report' | 'export' | 'import' | 'share' | 'print' | 'email' | 'select',
  boolean
>

export type DocMeta = {
  name: string
  label?: string
  module?: string
  istable?: 1
  issingle?: 1
  is_submittable?: 1
  is_tree?: 1
  in_create?: 1
  read_only?: 1
  allow_import?: 1
  allow_rename?: 1
  allow_copy?: 1
  track_changes?: 1
  editable_grid?: 1
  quick_entry?: 1
  title_field?: string
  image_field?: string
  search_fields?: string
  sort_field?: string
  sort_order?: string
  max_attachments?: number
  default_print_format?: string
  fields: DocField[]
  can: Permissions
  can_if_owner?: Partial<Permissions>
  permlevels?: number[]
  children?: Record<string, { fields: DocField[]; editable_grid?: 1; istable?: 1 }>
  no_access?: { reason: string; needs: string[] }
}

/** A document is whatever its DocType says it is, so this is honest about it. */
export type Doc = Record<string, unknown> & { name?: string; docstatus?: 0 | 1 | 2; modified?: string }

export type ListAnswer = { rows: Doc[]; start: number; limit: number; has_more: boolean; total?: number }

/** Where to go and put a refusal right, when it names a setting.
 *  ERPNext writes "please set X in <link>"; the gateway reads the link out of
 *  the markup it strips, so the sentence and the way to act on it arrive
 *  together instead of the second one being thrown away. */
export type ErrorFix = { doctype: string; name: string; label: string }

export type ApiError = {
  code: string
  message: string
  fields: Record<string, string>
  retryable: boolean
  request_id: string
  status: number
  fix?: ErrorFix
}

/** Frappe's three document states, named so nobody has to remember 0/1/2. */
export const DOCSTATUS = { draft: 0, submitted: 1, cancelled: 2 } as const

export const LAYOUT_TYPES: ReadonlySet<string> = new Set([
  'Section Break', 'Column Break', 'Tab Break', 'Heading', 'HTML',
])

/** A report, its filters, and what running it gives back. */
export type ReportRow = { name: string; ref_doctype?: string; report_type?: string; module?: string }

export type ReportDefinition = {
  name: string
  ref_doctype?: string
  report_type?: string
  module?: string
  /** Read out of the report's own JavaScript, without running any of it. */
  filters: DocField[]
  /** True when the report's script could not be read and these are the
   *  filters most accounting reports take, rather than this one's own. */
  filters_are_a_fallback: boolean
}

export type ReportColumn = {
  label?: string
  fieldname?: string
  fieldtype?: FieldType | string
  options?: string
  width?: number
  hidden?: 0 | 1
}

export type ReportAnswer = {
  columns: ReportColumn[]
  rows: (Record<string, unknown> | unknown[])[]
  total_rows: number
  truncated: boolean
  message?: string
  report_summary?: { label?: string; value?: unknown; indicator?: string; datatype?: string }[]
  add_total_row?: boolean
  execution_time?: number
}

/** A number card, with its value already worked out by the gateway.
 *  `ok: false` means the figure could not be produced — which is a different
 *  fact from zero, and must not be shown as one. */
export type NumberCard = {
  name: string
  label?: string
  document_type?: string
  function?: string
  module?: string
  color?: string
  value?: number | null
  ok?: boolean
  why?: string
}

/** Reading a spreadsheet before anything is written. */
export type ImportPreview = {
  doctype: string
  headers: string[]
  /** Column heading -> fieldname, or null where we could not guess. */
  mapping: Record<string, string | null>
  unmatched: string[]
  rows: number
  sample: unknown[][]
  fields: { fieldname: string; label?: string; required: boolean; type: string }[]
  required_missing: string[]
}

export type ImportResult = {
  dry_run: boolean
  doctype: string
  total: number
  succeeded: number
  done: { row: number; name?: string }[]
  failed: { row: number; why: string; fields?: Record<string, string> }[]
}

/** A bill of materials, read four ways. */
export type BomNode = {
  item_code: string
  item_name?: string
  bom_no: string | null
  qty: number
  stock_qty: number
  uom?: string
  expandable: boolean
  phantom: boolean
  subcontracted: boolean
}

export type BomExploded = {
  bom: string
  item: string
  quantity: number
  items: { item_code: string; item_name?: string; qty: number; uom?: string
           rate?: number; amount?: number }[]
}

export type BomCost = {
  bom: string
  item: string
  quantity: number
  currency?: string
  raw_material_cost?: number
  operating_cost?: number
  scrap_material_cost?: number | null
  total_cost?: number
  priced_from?: string
  operations: { operation?: string; workstation?: string; minutes?: number
                hour_rate?: number; cost?: number }[]
}

export type BomUse = {
  bom: string
  makes?: string
  makes_name?: string
  qty?: number
  uom?: string
  active: boolean
  default: boolean
  total_cost?: number
}

/** What an admin may change about a form, without a release. */
export type CustomiseSurface = {
  doctype: string
  label?: string
  naming_rule?: string
  autoname?: string
  custom_fields: {
    name: string; fieldname: string; label?: string; fieldtype: string
    options?: string; insert_after?: string; reqd?: 0 | 1; hidden?: 0 | 1
    read_only?: 0 | 1; in_list_view?: 0 | 1; description?: string
  }[]
  changes: { name: string; field_name: string; property: string; value: string }[]
  fields: { fieldname: string; label?: string; fieldtype: string }[]
  addable: { fieldtype: string; label: string }[]
  changeable: { property: string; label: string }[]
}

export type Person = {
  email: string
  roles: string[]
  disabled: boolean
  refreshed_at?: string
}

/** What a role can actually open — the question a role's name never answers. */
export type RoleReach = {
  role: string
  reaches: number
  modules: { module: string; can_change: string[]; can_only_read: string[] }[]
}

/** Rules a tenant has written for one form. */
export type AutomationRules = {
  doctype: string
  workflows: {
    name: string; workflow_name: string; is_active: 0 | 1; workflow_state_field?: string
    states: { state: string; doc_status?: string; allow_edit?: string }[]
    transitions: { state: string; action: string; next_state: string
                   allowed?: string; condition?: string }[]
  }[]
  notifications: { name: string; subject?: string; event?: string; enabled?: 0 | 1 }[]
  assignment_rules: { name: string; disabled?: 0 | 1; rule?: string; description?: string }[]
  webhooks: { name: string; webhook_docevent?: string; request_url?: string; enabled?: 0 | 1 }[]
}

export type ContactRole = { doctype: string; id: string; what: string }

export type ContactList = {
  q: string
  count: number
  more_than_one_role: number
  people: { name: string; roles: ContactRole[] }[]
}

export type ContactAbout = {
  doctype: string; name: string; label: string
  also: ContactRole[]
  contacts: { name: string; first_name?: string; last_name?: string; email_id?: string
              mobile_no?: string; designation?: string; is_primary_contact?: 0 | 1 }[]
  addresses: { name: string; address_line1?: string; city?: string; state?: string
               pincode?: string; country?: string; address_type?: string
               is_primary_address?: 0 | 1 }[]
}

export type FoundDoc = {
  doctype: string; name: string; title: string
  /** What it is, in words — "SINV-2026-00004" alone says nothing. */
  what: string
  exact: boolean
}

export type Notice = {
  id: string; subject: string; kind?: string
  doctype?: string; name?: string
  read: boolean; when?: string; from?: string
}

export type EmailThread = {
  doctype: string; name: string
  emails: {
    id: string; subject?: string; body: string
    from?: string; from_name?: string; to: string[]; cc: string[]
    when?: string; outgoing: boolean; status?: string; seen: boolean
  }[]
  /** False when no outgoing account is set up — said plainly rather than
   *  letting a reply vanish into a queue with nothing behind it. */
  can_send: boolean
}

/** Which parts of the ERP an organisation has.
 *
 *  `read_only` is the important one: a lapsed subscription keeps every screen
 *  and every report and refuses only the writes. An ERP that hides a
 *  tenant's own ledger over a card failure is holding a business hostage. */
export type ModuleState = {
  org?: string | number
  always: string[]
  areas: {
    key: string; label: string; what: string; modules: string[]
    state: 'on' | 'read_only' | 'off'
    can_write: boolean
  }[]
  lapsed_means: string
}

export type GstMonth = {
  company: string; from_date: string; to_date: string
  parts: {
    key: string; report: string; for: string; why: string
    rows: number
    columns: { field?: string; label?: string }[]
    sample?: unknown[]
    totals?: unknown[]
    /** Named rather than hidden: a tenant without e-invoicing has no
     *  e-invoice summary, and that is a fact about them, not a failure. */
    not_available?: string
  }[]
  note: string
}


/** One figure on the overview, with where it came from.
 *
 *  `basis` is a sentence, not a formula: the point is that somebody who does
 *  not trust the number can find out how it was worked out without asking. */
export type OverviewTile = {
  key: string
  label: string
  kind: 'money' | 'count'
  basis: string
  value: number | null
  ok: boolean
  why?: string
  /** Where the rows behind it live. */
  doctype?: string
  report?: string
  emphasis?: boolean
}

export type Overview = {
  period: { fiscal_year: string | null; from_date: string | null; to_date: string | null }
  company: string | null
  companies: string[]
  fiscal_years: { name: string; year_start_date: string; year_end_date: string }[]
  tiles: OverviewTile[]
}


/** A page or a form on the tenant's own public website. */
export type WebThing = {
  name: string
  title: string
  route: string
  published: boolean
  /** Where it answers: absolute when the tenant has set their own domain,
   *  otherwise just the path, because inventing a host produces a dead link. */
  address: string
  modified?: string
  /** Pages only. */
  content_type?: string
  /** Forms only: the DocType it writes into, and how many rows are there.
   *  `received` is null when we may not look, which is not zero. */
  doctype?: string
  received?: number | null
  login_required?: boolean
}

export type WebsiteAnswer = {
  pages: WebThing[]
  forms: WebThing[]
  home_page: string
  address_prefix: string
  shop: { available: boolean; why?: string }
}
